// CONTRACT:
// PURPOSE: [Лид-карточка главной: табы «Подобрать охрану»/«Получить аналитику»]
// SCOPE: [landing, lead, saveLeadCapture, lead_captures]
// AI_REFLECTION: [SCORE: Q=9, R=8, M=9] [DEPTH: standard]
//   [NOTE: Реальный лид-механизм — server action saveLeadCapture (таблица
//    lead_captures, VALID_SOURCES: hero_cta / landing_form). Валидация:
//    телефон ≥ 10 цифр, email — только если заполнен.]

"use client";

import { useState } from "react";
import { saveLeadCapture } from "@/app/actions/lead-actions";

type Intent = "guard" | "analytics";

const INTENT_LABEL: Record<Intent, string> = {
  guard: "Подобрать охрану",
  analytics: "Получить аналитику",
};

function digits(s: string) {
  return (s.match(/\d/g) || []).length;
}
function validEmail(s: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}

export function LeadFormCard({ source }: { source: "hero_cta" | "landing_form" }) {
  const [intent, setIntent] = useState<Intent>("guard");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [phoneErr, setPhoneErr] = useState(false);
  const [emailErr, setEmailErr] = useState(false);
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  async function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    const badP = digits(phone) < 10;
    setPhoneErr(badP);
    let badE = false;
    if (email.trim()) {
      badE = !validEmail(email.trim());
      setEmailErr(badE);
    } else {
      setEmailErr(false);
    }
    if (badP || badE || pending) return;

    setPending(true);
    setServerError(null);
    const contact = email.trim() ? `${phone.trim()} · ${email.trim()}` : phone.trim();
    const res = await saveLeadCapture(
      `Заявка с главной: ${INTENT_LABEL[intent]}`,
      contact,
      source
    );
    setPending(false);
    if (res.success) setDone(true);
    else setServerError(res.error || "Не удалось отправить заявку. Попробуйте ещё раз.");
  }

  if (done) {
    return (
      <div className="border border-[hsl(var(--portal-border))] bg-[hsl(var(--portal-surface))] p-7 text-center">
        <div className="font-mono text-lg font-bold text-[hsl(var(--portal-accent))]">
          Заявка принята
        </div>
        <p className="mt-2 text-sm text-[hsl(var(--portal-text-secondary))]">
          Спасибо. Свяжемся с вами по указанному телефону в рабочее время.
        </p>
      </div>
    );
  }

  return (
    <div className="border border-[hsl(var(--portal-border-hover))] bg-[hsl(var(--portal-surface))] p-[26px]">
      {/* Табы интента */}
      <div
        className="mb-5 flex gap-1 border-b border-[hsl(var(--portal-border))]"
        role="tablist"
        aria-label="Тип заявки"
      >
        {(Object.keys(INTENT_LABEL) as Intent[]).map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={intent === t}
            onClick={() => setIntent(t)}
            className={`-mb-px border-b-2 px-3.5 py-2.5 font-mono text-[13px] transition-colors ${
              intent === t
                ? "border-[hsl(var(--portal-accent))] text-[hsl(var(--portal-text))]"
                : "border-transparent text-[hsl(var(--portal-text-muted))] hover:text-[hsl(var(--portal-text-secondary))]"
            }`}
          >
            {INTENT_LABEL[t]}
          </button>
        ))}
      </div>

      <form noValidate onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 gap-3.5 min-[520px]:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="lead-phone"
              className="font-mono text-xs text-[hsl(var(--portal-text-muted))]"
            >
              Телефон
            </label>
            <input
              id="lead-phone"
              name="phone"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="+7 ___ ___-__-__"
              required
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className={`w-full bg-[hsl(var(--portal-bg))] px-3.5 py-3 font-sans text-[15px] text-[hsl(var(--portal-text))] outline-none transition-colors border ${
                phoneErr
                  ? "border-[hsl(var(--portal-danger))]"
                  : "border-[hsl(var(--portal-border-hover))] focus:border-[hsl(var(--portal-accent))]"
              }`}
            />
            {phoneErr && (
              <span className="font-mono text-[11.5px] text-[hsl(var(--portal-danger))]">
                Укажите телефон (минимум 10 цифр)
              </span>
            )}
          </div>
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="lead-email"
              className="font-mono text-xs text-[hsl(var(--portal-text-muted))]"
            >
              Email
            </label>
            <input
              id="lead-email"
              name="email"
              type="email"
              autoComplete="email"
              placeholder="you@company.ru"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`w-full bg-[hsl(var(--portal-bg))] px-3.5 py-3 font-sans text-[15px] text-[hsl(var(--portal-text))] outline-none transition-colors border ${
                emailErr
                  ? "border-[hsl(var(--portal-danger))]"
                  : "border-[hsl(var(--portal-border-hover))] focus:border-[hsl(var(--portal-accent))]"
              }`}
            />
            {emailErr && (
              <span className="font-mono text-[11.5px] text-[hsl(var(--portal-danger))]">
                Проверьте адрес email
              </span>
            )}
          </div>
        </div>

        <button
          type="submit"
          disabled={pending}
          className="mt-5 w-full border border-[hsl(var(--portal-border-hover))] bg-[hsl(var(--portal-surface-2))] px-5 py-3 font-mono text-sm font-semibold text-[hsl(var(--portal-text))] transition-colors hover:border-[hsl(var(--portal-text-dim))] hover:bg-[hsl(var(--portal-surface))] disabled:opacity-60"
        >
          {pending ? "Отправляем…" : INTENT_LABEL[intent]}
        </button>

        {serverError && (
          <p className="mt-3 font-mono text-xs text-[hsl(var(--portal-danger))]">
            {serverError}
          </p>
        )}

        <p className="mt-3 font-mono text-[13px] text-[hsl(var(--portal-text-dim))]">
          Нажимая кнопку, вы соглашаетесь на обработку контактов
          согласно <a href="/privacy" className="text-[hsl(var(--portal-accent))] hover:underline">политике конфиденциальности</a>.
        </p>
      </form>
    </div>
  );
}

/**
 * Компактная телефонная форма финального CTA (только телефон).
 * Тот же механизм lead_captures, источник landing_form.
 */
export function CtaPhoneForm() {
  const [phone, setPhone] = useState("");
  const [err, setErr] = useState(false);
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);

  async function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    if (digits(phone) < 10) {
      setErr(true);
      return;
    }
    setErr(false);
    setPending(true);
    const res = await saveLeadCapture(
      "Заявка с главной: подобрать охрану",
      phone.trim(),
      "landing_form"
    );
    setPending(false);
    if (res.success) setDone(true);
  }

  if (done) {
    return (
      <div className="border border-[hsl(var(--portal-border))] bg-[hsl(var(--portal-bg))] px-6 py-5 font-mono text-sm font-bold text-[hsl(var(--portal-accent))]">
        Заявка принята — свяжемся с вами.
      </div>
    );
  }

  return (
    <form noValidate onSubmit={handleSubmit} className="flex flex-wrap gap-2.5">
      <input
        type="tel"
        inputMode="tel"
        name="phone"
        placeholder="+7 ___ ___-__-__"
        aria-label="Телефон"
        required
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        className={`min-w-[220px] flex-[1_1_220px] border bg-[hsl(var(--portal-bg))] px-4 py-3 font-sans text-[15px] text-[hsl(var(--portal-text))] outline-none transition-colors ${
          err
            ? "border-[hsl(var(--portal-danger))]"
            : "border-[hsl(var(--portal-border-hover))] focus:border-[hsl(var(--portal-accent))]"
        }`}
      />
      <button
        type="submit"
        disabled={pending}
        data-ym-goal="landing_lead_cta"
        className="border border-transparent bg-[hsl(var(--portal-accent))] px-5 py-3 font-mono text-sm font-semibold text-[hsl(var(--portal-accent-on))] shadow-[4px_4px_0_0_hsl(225_14%_3%)] transition-all hover:-translate-x-0.5 hover:-translate-y-0.5 hover:bg-[hsl(var(--portal-accent-hover))] hover:shadow-[6px_6px_0_0_hsl(225_14%_3%)] active:translate-x-0 active:translate-y-0 active:shadow-[2px_2px_0_0_hsl(225_14%_3%)] disabled:opacity-60"
      >
        {pending ? "Отправляем…" : "Отправить заявку"}
      </button>
    </form>
  );
}
